import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { SubscriptionTier } from "@/hooks/useSubscription";
import { 
  Heart, 
  Search, 
  Plus, 
  Trash2, 
  Mail, 
  Phone, 
  DollarSign,
  MessageSquare,
  UserPlus,
  MapPin
} from "lucide-react";

export interface Consultant {
  id: string;
  name: string;
  company?: string;
  specialty: string;
  email?: string;
  phone?: string;
  hourlyRate?: number;
  currency?: string;
  bio?: string;
  location?: string;
  rate?: string;
  addedAt?: any;
  status?: 'available' | 'engaged' | 'inactive';
}

interface ConsultantsTabProps {
  consultants: Consultant[];
  favoriteConsultants: string[];
  onAddClick?: () => void;
  onRemove?: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onContact?: (id: string) => void;
  language: 'en' | 'fr';
  formatCurrency?: (amount: number, currency?: string) => string;
  showAddButton?: boolean;
  /** If set, wraps contact/favorite/add actions behind a SubscriptionGate */
  gateContactTier?: SubscriptionTier;
}

const translations = {
  en: {
    myConsultants: "My Consultants",
    consultantsDesc: "Manage your professional advisors",
    addConsultant: "Add Consultant",
    noConsultants: "No consultants found",
    addFirstConsultant: "Add Your First Consultant",
    specialty: "Specialty",
    contact: "Contact",
    favorite: "❤️ Favorite",
    add: "🤍 Add",
    searchPlaceholder: "Search (e.g., tax, corporate, QC...)",
    allStatuses: "All statuses",
    available: "Available",
    engaged: "Engaged",
    inactive: "Inactive",
    consultantsFound: "consultant(s) found",
    rate: "Rate",
    tip: "Tip: The heart ❤️ adds the consultant to your personal list. \"Contact\" creates a request and will later open a calendar.",
    upgradeInline: "Subscribe to the Essential plan to contact this consultant.",
    upgradeLink: "See plans",
    upgradeFooter: "🔒 Contacting consultants requires the Essential plan or higher."
  },
  fr: {
    myConsultants: "Mes Consultants",
    consultantsDesc: "Gérez vos conseillers professionnels",
    addConsultant: "Ajouter un Consultant",
    noConsultants: "Aucun consultant trouvé",
    addFirstConsultant: "Ajouter votre premier Consultant",
    specialty: "Spécialité",
    contact: "Contacter",
    favorite: "❤️ Favori",
    add: "🤍 Ajouter",
    searchPlaceholder: "Rechercher (ex: fiscalité, corporate, QC...)",
    allStatuses: "Tous les statuts",
    available: "Disponible",
    engaged: "Engagé",
    inactive: "Inactif",
    consultantsFound: "consultant(s) trouvé(s)",
    rate: "Tarif",
    tip: "Astuce : le cœur ❤️ ajoute le consultant à ta liste personnelle. \"Contacter\" crée une demande et pourra plus tard ouvrir un calendrier.",
    upgradeInline: "Souscrivez au forfait Essential pour contacter ce consultant.",
    upgradeLink: "Voir les forfaits",
    upgradeFooter: "🔒 Contacter les consultants nécessite le forfait Essential ou supérieur."
  }
};

export function ConsultantsTab({
  consultants,
  favoriteConsultants,
  onAddClick,
  onRemove,
  onToggleFavorite,
  onContact,
  language,
  formatCurrency,
  showAddButton = true,
  gateContactTier
}: ConsultantsTabProps) {
  const t = translations[language];
  const navigate = useNavigate();
  const { tier } = useSubscription();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "available" | "engaged" | "inactive">("all");

  const TIER_ORDER: Record<string, number> = { visitor: 0, byod: 1, essential: 2, 'essential-plus': 3, pro: 4 };
  const showUpgradeHint = gateContactTier && (TIER_ORDER[tier ?? 'visitor'] ?? 0) < (TIER_ORDER[gateContactTier] ?? 0);

  const isConsultantFavorite = (id: string) => favoriteConsultants.includes(id);

  const filteredConsultants = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return consultants.filter((c) => {
      const matchesSearch = !s ||
        [c.name, c.specialty, c.company, c.bio, c.location]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(s));
      
      const matchesFilter = statusFilter === "all" || c.status === statusFilter;
      
      return matchesSearch && matchesFilter;
    });
  }, [consultants, searchTerm, statusFilter]);

  const getStatusLabel = (status?: string) => {
    if (status === 'available') return language === 'fr' ? 'Disponible' : 'Available';
    if (status === 'engaged') return language === 'fr' ? 'Engagé' : 'Engaged';
    if (status === 'inactive') return language === 'fr' ? 'Inactif' : 'Inactive';
    return null;
  };

  const handleContact = (id: string) => {
    if (onContact) {
      onContact(id);
    } else {
      alert(language === 'fr' ? "Demande de contact envoyée ✅" : "Contact request sent ✅");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t.myConsultants}</CardTitle>
            <CardDescription>{t.consultantsDesc}</CardDescription>
          </div>
          {showAddButton && onAddClick && (
            <Button onClick={onAddClick}>
              <Plus className="h-4 w-4 mr-1" />
              {t.addConsultant}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            className="border rounded-md px-3 py-2 bg-background"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">{t.allStatuses}</option>
            <option value="available">{t.available}</option>
            <option value="engaged">{t.engaged}</option>
            <option value="inactive">{t.inactive}</option>
          </select>
        </div>

        {/* Results count */}
        <div className="text-sm text-muted-foreground">
          {filteredConsultants.length} {t.consultantsFound}
        </div>

        {filteredConsultants.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">{t.noConsultants}</p>
            {showAddButton && onAddClick && (
              <Button onClick={onAddClick}>{t.addFirstConsultant}</Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConsultants.map(consultant => (
              <Card key={consultant.id} className="hover:shadow-lg transition-shadow border rounded-lg bg-gradient-to-br from-background to-blue-50/30">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {consultant.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{consultant.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{consultant.specialty}</p>
                      {consultant.location && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {consultant.location}
                        </p>
                      )}
                    </div>
                    {consultant.status && getStatusLabel(consultant.status) && (
                      <Badge 
                        variant={consultant.status === 'available' ? 'default' : consultant.status === 'engaged' ? 'secondary' : 'outline'}
                        className="text-xs"
                      >
                        {getStatusLabel(consultant.status)}
                      </Badge>
                    )}
                  </div>

                  {consultant.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{consultant.bio}</p>
                  )}

                  <div className="space-y-1 text-sm">
                    {consultant.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{consultant.email}</span>
                      </div>
                    )}
                    {consultant.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{consultant.phone}</span>
                      </div>
                    )}
                  </div>

                  {(consultant.hourlyRate || consultant.rate) && (
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {consultant.rate || (formatCurrency 
                          ? `${formatCurrency(consultant.hourlyRate!, consultant.currency || 'USD')}/h`
                          : `$${consultant.hourlyRate}/h`
                        )}
                      </span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t">
                    <Button 
                      size="sm" 
                      variant={isConsultantFavorite(consultant.id) ? "default" : "outline"}
                      className={isConsultantFavorite(consultant.id) ? "bg-red-500 hover:bg-red-600" : ""}
                      onClick={() => onToggleFavorite(consultant.id)}
                    >
                      {isConsultantFavorite(consultant.id) ? t.favorite : t.add}
                    </Button>
                    {gateContactTier ? (
                      <SubscriptionGate requiredTier={gateContactTier} silent>
                        <Button 
                          size="sm" 
                          variant="default"
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleContact(consultant.id)}
                        >
                          {t.contact}
                        </Button>
                      </SubscriptionGate>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="default"
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleContact(consultant.id)}
                      >
                        {t.contact}
                      </Button>
                    )}
                    {onRemove && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive"
                        onClick={() => onRemove(consultant.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {showUpgradeHint && (
                    <p className="text-xs text-muted-foreground pt-1">
                      {t.upgradeInline}{' '}
                      <button 
                        onClick={() => navigate('/buyer/payment')} 
                        className="text-primary underline hover:text-primary/80 font-medium"
                      >
                        {t.upgradeLink} →
                      </button>
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          {t.tip}
        </p>
        {showUpgradeHint && (
          <p className="text-sm text-muted-foreground mt-2 p-3 rounded-md bg-muted/50 border border-border">
            {t.upgradeFooter}{' '}
            <button 
              onClick={() => navigate('/buyer/payment')} 
              className="text-primary underline hover:text-primary/80 font-medium"
            >
              {t.upgradeLink} →
            </button>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
